import { useIsDark } from '@highlight/ui'
import type { LineupPlanId } from '@highlight/common'
import type { SelectableV2Plan } from './plan-select-cards'

// From the prism prototype new-pricing-model/v2: a shape per plan, each digit indexing its four-colour palette.
export type MosaicPalette = readonly [string, string, string, string]

export interface PlanMosaic {
  grid: ReadonlyArray<string>
  light: MosaicPalette
  dark: MosaicPalette
}

export const PLAN_MOSAICS: Record<SelectableV2Plan, PlanMosaic> = {
  lite: {
    grid: ['...1.', '..1..', '.000.', '00000', '.000.'],
    light: ['oklch(0.72 0.15 60)', 'oklch(0.8 0.11 40)', 'oklch(0.86 0.12 85)', 'oklch(0.9 0.06 70)'],
    dark: ['oklch(0.66 0.13 60)', 'oklch(0.58 0.11 40)', 'oklch(0.72 0.11 85)', 'oklch(0.45 0.06 70)'],
  },
  pro2: {
    grid: ['.020.', '00200', '00200', '.020.', '.1.1.', '..0..'],
    light: ['oklch(0.72 0.17 140)', 'oklch(0.8 0.13 125)', 'oklch(0.86 0.09 165)', 'oklch(0.9 0.06 140)'],
    dark: ['oklch(0.66 0.13 140)', 'oklch(0.58 0.11 120)', 'oklch(0.72 0.09 165)', 'oklch(0.45 0.06 140)'],
  },
  max: {
    grid: ['....000....', '...00000.22', '2222222222.', '..0000000..', '22.00000...', '....000....'],
    light: ['oklch(0.68 0.14 255)', 'oklch(0.8 0.09 235)', 'oklch(0.84 0.08 285)', 'oklch(0.9 0.05 255)'],
    dark: ['oklch(0.66 0.12 255)', 'oklch(0.58 0.09 235)', 'oklch(0.72 0.08 285)', 'oklch(0.45 0.05 255)'],
  },
}

/** Each plan's skyline on its 6×6 grid: a couple of rows on Free, about half on Pro, nearly full on Max. */
const SKYLINES: Record<SelectableV2Plan, ReadonlyArray<number>> = {
  lite: [2, 1, 1, 2, 1, 1],
  pro2: [4, 3, 3, 4, 4, 3],
  max: [6, 6, 5, 6, 6, 6],
}

const SKYLINE_SIDE = 6
const SKYLINE_CELL_PX = 6

/** Which palette step a cell takes: the lead most often. Fixed by the cell's place, so it never reshuffles. */
function cellStep(column: number, row: number, seed: number): number {
  const hash = Math.imul(column * 374761393 + row * 668265263 + seed * 97, 1274126177)
  const unit = ((hash ^ (hash >>> 16)) >>> 0) / 4294967296
  return unit < 0.4 ? 0 : unit < 0.65 ? 1 : unit < 0.85 ? 2 : 3
}

/** The plan's mark on Usage & billing: a skyline of cells that rises with the plan. */
export function PlanSkyline({ plan, className }: { plan: LineupPlanId; className?: string }) {
  const isDark = useIsDark()
  const key: SelectableV2Plan = plan === 'free' ? 'lite' : plan
  const palette = isDark ? PLAN_MOSAICS[key].dark : PLAN_MOSAICS[key].light
  const seed = { lite: 1, pro2: 2, max: 3 }[key]
  const size = SKYLINE_SIDE * SKYLINE_CELL_PX
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={className}>
      {SKYLINES[key].flatMap((height, column) =>
        Array.from({ length: height }, (_, row) => (
          <rect
            key={`${column}-${row}`}
            x={column * SKYLINE_CELL_PX + 0.5}
            y={(SKYLINE_SIDE - 1 - row) * SKYLINE_CELL_PX + 0.5}
            width={SKYLINE_CELL_PX - 1}
            height={SKYLINE_CELL_PX - 1}
            fill={palette[cellStep(column, row, seed)]}
          />
        )),
      )}
    </svg>
  )
}
